import { getMachineLogs } from '@/redux/actions/gpuActions';
import Badge from '@/shared/components/Badge';
import { colorText, colorTextAdditional } from '@/utils/palette';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import HardwareSpecifications from './GpuOneHardwareSpecifications';
import LogsTable from './GpuOneLogsTable';
import NetworkInterfaces from './GpuOneNetworkInterfaces';
import StorageDisks from './GpuOneStorageDisks';
import SystemInformation from './GpuOneSystemInformation';

const GpuOneDetailView = ({ gpu, onBack }) => {
	const dispatch = useDispatch();
	const { gpuOneMaasLogs } = useSelector(state => state.gpuOne);

	useEffect(() => {
		if (gpu?.system_serial) {
			dispatch(getMachineLogs(gpu.system_serial));
		}
	}, [dispatch, gpu?.system_serial]);

	if (!gpu) return null;

	// Parse network interfaces
	const parseNetworkInterfaces = networkInterfaces => {
		if (!networkInterfaces || !Array.isArray(networkInterfaces)) return [];
		return networkInterfaces
			.map(iface => {
				const match = iface
					.trim()
					.match(/^([^(]+)\(([^)]+)\)(?: \[IPs: ([^\]]+)\])?/);
				if (match) {
					return {
						name: match[1].trim(),
						mac: match[2].trim(),
						ip: match[3] || null,
					};
				}
				return null;
			})
			.filter(Boolean);
	};

	// Parse storage disks
	const parseStorageDisks = storageDisks => {
		if (!storageDisks || !Array.isArray(storageDisks)) return [];
		return storageDisks
			.map(disk => {
				const match = disk.trim().match(/^([^(]+)\(([^,]+), ([^)]+)\)/);
				if (match) {
					return {
						name: match[1].trim(),
						size: match[2].trim(),
						model: match[3].trim(),
					};
				}
				return null;
			})
			.filter(Boolean);
	};

	const networkInterfaces = parseNetworkInterfaces(gpu.network_interfaces);
	const storageDisks = parseStorageDisks(gpu.storage_disks);
	const totalStorage = storageDisks.reduce((sum, disk) => {
		const size = parseFloat(disk.size.replace('GB', ''));
		return sum + size;
	}, 0);

	const logs = gpuOneMaasLogs[gpu.system_serial]?.logs || [];

	return (
		<Container>
			{/* Header */}
			<Row>
				<Col>
					<DetailHeader>
						<div>
							<BackButton onClick={onBack}>← Back to Server List</BackButton>
							<h3 className="page-title">{gpu.machine_name}</h3>
							<DetailSubtitle>{gpu.hostname}</DetailSubtitle>
						</div>
						<StatusSection>
							<Badge bg={gpu.machine_status}>{gpu.machine_status}</Badge>
							<LastUpdated>
								Last updated:{' '}
								{dayjs(gpu.last_updated).format('MM/DD/YYYY hh:mm A')}
							</LastUpdated>
						</StatusSection>
					</DetailHeader>
				</Col>
			</Row>

			{/* Main Content */}
			<Row>
				<HardwareSpecifications
					gpu={gpu}
					totalStorage={totalStorage}
					storageDisks={storageDisks}
					networkInterfaces={networkInterfaces}
				/>
				<SystemInformation gpu={gpu} />
			</Row>
			<Row>
				<NetworkInterfaces
					networkInterfaces={networkInterfaces}
					isSort={true}
				/>
				<StorageDisks storageDisks={storageDisks} isSort={true} />
			</Row>
			<Row>
				<LogsTable logs={logs} />
			</Row>
		</Container>
	);
};

// Styled Components
const DetailHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 30px;
	padding: 20px 0;
`;

const BackButton = styled.button`
	background: none;
	border: none;
	color: #667eea;
	font-size: 1rem;
	cursor: pointer;
	padding: 0;
	margin-bottom: 10px;
	transition: color 0.3s ease;

	&:hover {
		color: #764ba2;
	}
`;

const DetailTitle = styled.h1`
	font-size: 2.5rem;
	font-weight: 700;
	color: ${colorText};
	margin-bottom: 8px;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
`;

const DetailSubtitle = styled.p`
	font-size: 1.1rem;
	color: ${colorTextAdditional};
	margin: 0;
`;

const StatusSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 10px;
`;

const LastUpdated = styled.span`
	font-size: 0.9rem;
	color: ${colorTextAdditional};
`;

export default GpuOneDetailView;
